// ── Touch Joystick ──
// Virtual joystick for movement using nipplejs.
// Positioned on the left side of the screen.

import nipplejs from 'nipplejs';
import type { JoystickManager, EventData, JoystickOutputData } from 'nipplejs';
import { settingsStore } from '../core/settings';

export interface JoystickMovement {
  forward: boolean;
  back: boolean;
  left: boolean;
  right: boolean;
  walk: boolean;
}

const WALK_THRESHOLD = 0.4;
const DEFAULT_DEADZONE = 0.2;

export class TouchJoystick {
  private manager: JoystickManager | null = null;
  private zone: HTMLDivElement | null = null;

  private forward = false;
  private back = false;
  private left = false;
  private right = false;
  private force = 0;

  // Bound handler refs for cleanup
  private readonly onMove: (evt: EventData, data: JoystickOutputData) => void;
  private readonly onEnd: () => void;

  /** MOB-L2: Per-instance deadzone (0..0.5). Read from settingsStore. */
  private deadzone: number = DEFAULT_DEADZONE;

  constructor() {
    // MOB-L2: Read initial deadzone from settings (safe in environments
    // without window — settingsStore guards localStorage internally).
    try {
      const stored = settingsStore.get().touchJoystickDeadzone;
      if (typeof stored === 'number' && Number.isFinite(stored)) {
        this.deadzone = Math.max(0, Math.min(0.5, stored));
      }
    } catch {
      // settingsStore unavailable — fall back to default.
    }

    this.onMove = (_evt: EventData, data: JoystickOutputData) => {
      this.force = data.force;
      // Angle in radians, 0 = right, PI/2 = up
      const angle = data.angle.radian;
      // Decompose into cardinal directions using thresholds
      // forward/back = Y component, left/right = X component
      const x = Math.cos(angle) * data.force;
      const y = Math.sin(angle) * data.force;

      const deadZone = this.deadzone;
      this.forward = y > deadZone;
      this.back = y < -deadZone;
      this.right = x > deadZone;
      this.left = x < -deadZone;
    };

    this.onEnd = () => {
      this.forward = false;
      this.back = false;
      this.left = false;
      this.right = false;
      this.force = 0;
    };

    this.createJoystick();
  }

  private createJoystick(): void {
    // Create container zone on left side
    this.zone = document.createElement('div');
    this.zone.id = 'touch-joystick-zone';
    Object.assign(this.zone.style, {
      position: 'fixed',
      left: '0',
      bottom: '0',
      width: '40%',
      height: '50%',
      zIndex: '1000',
      touchAction: 'none',
    } satisfies Partial<CSSStyleDeclaration>);
    document.body.appendChild(this.zone);

    this.manager = nipplejs.create({
      zone: this.zone,
      mode: 'static',
      position: { left: '15%', bottom: '25%' },
      size: 120,
      color: 'rgba(255, 255, 255, 0.4)',
      restOpacity: 0.5,
    });

    this.manager.on('move', this.onMove);
    this.manager.on('end', this.onEnd);
  }

  /** MOB-L2: Set the joystick deadzone (clamped to 0..0.5). */
  setDeadzone(value: number): void {
    if (!Number.isFinite(value)) return;
    this.deadzone = Math.max(0, Math.min(0.5, value));
  }

  /** MOB-L2: Read the current deadzone. */
  getDeadzone(): number {
    return this.deadzone;
  }

  /** Get current movement state. */
  getMovement(): JoystickMovement {
    return {
      forward: this.forward,
      back: this.back,
      left: this.left,
      right: this.right,
      walk: this.force > 0 && this.force < WALK_THRESHOLD,
    };
  }

  /** Show the joystick zone. */
  show(): void {
    if (this.zone) {
      this.zone.style.display = '';
    }
  }

  /** Hide the joystick zone. */
  hide(): void {
    if (this.zone) {
      this.zone.style.display = 'none';
    }
  }

  /** Remove joystick and clean up. Safe to call multiple times. */
  dispose(): void {
    if (!this.manager && !this.zone) return; // Already disposed
    if (this.manager) {
      this.manager.off('move', this.onMove);
      this.manager.off('end', this.onEnd);
      this.manager.destroy();
      this.manager = null;
    }
    if (this.zone) {
      this.zone.remove();
      this.zone = null;
    }
  }
}
